import React, { useEffect } from "react"
import { Row, Col, CardBody, Card, Container, Form, Input, FormFeedback, Label } from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { Link, useLocation, useNavigate } from "react-router-dom"
import * as Yup from "utils/yup"
import { useFormik } from "lib/formik"
import AuthBrandingLogo from "components/Authentication/AuthBrandingLogo"
import { verifyLoginOtp } from "store/actions/authAction"
import { showAlert } from "store/reducers/alertReducer"
import { useTranslation } from "react-i18next"
import { ROUTES } from "constants/routes"
import { getApiErrorMessage } from "utils/apiErrorMessage"
import useAuthPageBranding from "hooks/useAuthPageBranding"
import { resolvePostLoginRedirect } from "utils/accessibleRoutes"

const LoginOtp = () => {
  const { t } = useTranslation()
  const { brandTitle, authPageClassName } = useAuthPageBranding(t("Verify OTP"))
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const email = location?.state?.email || ""
  const redirectQuery = location?.state?.redirectQuery || ""
  const { loading } = useSelector(state => state.Login)

  useEffect(() => {
    if (!email) {
      navigate(ROUTES.LOGIN, { replace: true })
    }
  }, [email, navigate])

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      otp: "",
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .matches(/^\d{6}$/, t("Please enter a valid 6-digit OTP"))
        .required(t("Please enter OTP")),
    }),
    onSubmit: values => {
      dispatch(
        verifyLoginOtp({
          email,
          otp: values.otp,
        })
      ).then(action => {
        const payload = action?.payload || {}
        const { statusCode, status, data } = payload
        const isSuccess = statusCode === 200 && status
        dispatch(
          showAlert({
            text: isSuccess
              ? payload.msg
              : getApiErrorMessage(payload, null, {
                  isAuth: true,
                  fallback: t("Unable to verify OTP. Please try again."),
                }),
            type: isSuccess ? "success" : "error",
          })
        )
        if (statusCode === 200 && status && data) {
          const redirectTo = resolvePostLoginRedirect(redirectQuery, data)
          navigate(redirectTo, { replace: true })
        }
      })
    },
  })

  return (
    <div className={`account-pages ${authPageClassName}`}>
      <Container style={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Row className="justify-content-center login_form">
          <Col md={12}>
            <Card className="overflow-hidden">
              <div className="login-platform-hero">
                <Row>
                  <Col className="col-12">
                    <div className="p-4">
                      <h5>{brandTitle}</h5>
                      <p>{t("Enter the 6-digit OTP sent to your email.")}</p>
                    </div>
                  </Col>
                </Row>
              </div>
              <CardBody className="pt-0">
                <AuthBrandingLogo />
                <div className="p-2">
                  <Form
                    className="form-horizontal"
                    onSubmit={e => {
                      e.preventDefault()
                      validation.handleSubmit()
                      return false
                    }}
                  >
                    <div className="mb-3">
                      <Label className="form-label">{t("Email")}</Label>
                      <Input value={email} disabled />
                    </div>
                    <div className="mb-3">
                      <Label className="form-label">{t("OTP")}</Label>
                      <Input
                        name="otp"
                        className="form-control"
                        placeholder={t("Enter 6-digit OTP")}
                        type="text"
                        maxLength={6}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.otp || ""}
                        invalid={validation.touched.otp && validation.errors.otp ? true : false}
                      />
                      {validation.touched.otp && validation.errors.otp ? (
                        <FormFeedback type="invalid">{validation.errors.otp}</FormFeedback>
                      ) : null}
                    </div>
                    <div className="mt-3 d-grid">
                      <button className="btn btn-primary btn-block" type="submit" disabled={loading}>
                        {t("Verify OTP")}
                      </button>
                    </div>
                    <div className="mt-4 text-center">
                      <Link to={ROUTES.LOGIN} className="text-muted">
                        {t("Back to login")}
                      </Link>
                    </div>
                  </Form>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default LoginOtp
